import { type FC, type PropsWithChildren } from 'react'
import { LabelNormal } from '~/components/LabelNormal'
import { RatingStars } from '~/components/reviews/RatingStars'
import { SafariNewTabLink } from '~/components/safari/link'
import { MEAL_PERIOD_LABEL } from '~/lib/constants/constants'
import { format } from '~/lib/date/date'
import { isEmpty } from '~/lib/empty'
import type { FragmentType } from '~/lib/graphql'
import { getFragmentData } from '~/lib/graphql'
import { css, sva } from '~/styled-system/css'
import { ReviewsNodesFragment } from '../../reviewsNode.frag'
import { ReviewComments } from './ReviewComments'

type Props = {
  review: FragmentType<typeof ReviewsNodesFragment>
  isPc: boolean
}

export function ReviewComponent(props: Props) {
  return props.isPc ? <PcReviewComponent {...props} /> : <SdReviewComponent {...props} />
}

const pcReviewComponentStyles = sva({
  slots: [
    'container',
    'heading',
    'total',
    'summary',
    'information',
    'rating',
    'item',
    'title',
    'value',
  ],
  base: {
    container: {
      spaceY: '5',
      bg: 'white',
      p: '5',
      rounded: 'md',
    },
    heading: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'space-between',
    },
    total: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      columnGap: '2',
    },
    summary: {
      spaceY: '1',
    },
    information: {
      textAlign: 'right',
      fontSize: 'sm',
      color: 'gray.600',
      spaceY: '1',
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '4',
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '2',
      pr: '4',
      borderRight: 'light',
      _last: {
        pr: '0',
        borderRight: 'none',
      },
    },
    title: {
      fontSize: 'sm',
    },
    value: {
      fontSize: 'md',
      fontWeight: 'bold',
    },
  },
})

const PcReviewComponent: React.FC<Props> = ({ review: maskedReview }) => {
  const review = getFragmentData(ReviewsNodesFragment, maskedReview)
  const classes = pcReviewComponentStyles()

  return (
    <>
      <article
        itemProp="review"
        itemScope
        itemType="https://schema.org/Review"
        className={classes.container}
      >
        <div className={classes.heading}>
          <PcSummary review={maskedReview} />
          <PcInformation review={maskedReview}>
            <p className={css({ fontSize: 'sm' })}>
              <span>投稿日:</span>
              <time itemProp="datePublished" dateTime={review.userPostDate}>
                {format(review.userPostDate, 'yyyy/MM/dd')}
              </time>
            </p>
          </PcInformation>
        </div>
        <ReviewComments review={maskedReview} />
      </article>
    </>
  )
}

function PcSummary({ review: _review }: { review: FragmentType<typeof ReviewsNodesFragment> }) {
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const classes = pcReviewComponentStyles()

  if (review.__typename === 'LyReview') {
    return (
      <div className={classes.summary}>
        {review.totalValue !== 0 && (
          <div
            itemProp="reviewRating"
            itemScope
            itemType="https://schema.org/Rating"
            className={css({ display: 'flex', alignItems: 'baseline', gap: '1' })}
          >
            <RatingStars value={review.totalValue} size="md" />
            <div itemProp="ratingValue" className={css({ fontSize: '3xl', fontWeight: 'bold' })}>
              {review.totalValue.toFixed(1)}
            </div>
          </div>
        )}
      </div>
    )
  }
  return (
    <div className={classes.summary}>
      <div className={classes.total}>
        <LabelNormal text={MEAL_PERIOD_LABEL[review.mealPeriod]} />
        {review.rating !== undefined && (
          <div
            itemProp="reviewRating"
            itemScope
            itemType="https://schema.org/Rating"
            className={css({ display: 'flex', alignItems: 'baseline', gap: '1' })}
          >
            <RatingStars value={review.rating.totalValue} size="md" />
            <div itemProp="ratingValue" className={css({ fontSize: '3xl', fontWeight: 'bold' })}>
              {review.rating.totalValue.toFixed(1)}
            </div>
          </div>
        )}
      </div>
      {review.rating ? (
        <div className={classes.rating}>
          <p className={classes.item}>
            <span className={classes.title}>料理・味</span>
            <span className={classes.value}>{review.rating.food.toFixed(1)}</span>
          </p>
          <p className={classes.item}>
            <span className={classes.title}>雰囲気</span>
            <span className={classes.value}>{review.rating.atmosphere.toFixed(1)}</span>
          </p>
          <p className={classes.item}>
            <span className={classes.title}>接客・サービス</span>
            <span className={classes.value}>{review.rating.service.toFixed(1)}</span>
          </p>
          <p className={classes.item}>
            <span className={classes.title}>コストパフォーマンス</span>
            <span className={classes.value}>{review.rating.costPerformance.toFixed(1)}</span>
          </p>
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}

type InformationProps = PropsWithChildren<{ review: FragmentType<typeof ReviewsNodesFragment> }>

const PcInformation: FC<InformationProps> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgPcInformation : IkyuPcInformation

function HandleName({ handleName }: { handleName: string }) {
  return (
    <p itemProp="author" itemScope itemType="https://schema.org/Person">
      <span itemProp="name">{handleName}</span>
    </p>
  )
}

function PpgPcInformation({ review: _review, children }: InformationProps) {
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const classes = pcReviewComponentStyles()
  return (
    <div className={classes.information}>
      <HandleName handleName={review.handleName} />
      {children}
    </div>
  )
}

function IkyuPcInformation({ review: _review, children }: InformationProps) {
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const classes = pcReviewComponentStyles()

  if (review.__typename === 'LyReview' || isEmpty(review.memberHash)) {
    return (
      <div className={classes.information}>
        <HandleName handleName={review.handleName} />
        {children}
      </div>
    )
  }

  return (
    <div className={classes.information}>
      <p>
        <SafariNewTabLink href={`/reviews/user/${review.memberHash}/`}>
          <span itemProp="author" itemScope itemType="https://schema.org/Person">
            <span itemProp="name">{review.handleName}</span>
          </span>
        </SafariNewTabLink>
      </p>
      {children}
    </div>
  )
}

const sdReviewComponentStyles = sva({
  slots: ['container', 'heading', 'total', 'summary', 'information', 'rating'],
  base: {
    container: {
      spaceY: '3',
      bg: 'white',
      p: '3',
    },
    heading: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'space-between',
    },
    summary: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '1',
    },
    total: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      columnGap: '1',
    },
    information: {
      textAlign: 'right',
      fontSize: 'xs',
      color: 'gray.600',
      spaceY: '1',
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '4',
    },
  },
})

const SdReviewComponent: React.FC<Props> = ({ review: maskedReview }) => {
  const review = getFragmentData(ReviewsNodesFragment, maskedReview)
  const classes = sdReviewComponentStyles()
  return (
    <>
      <article
        itemProp="review"
        itemScope
        itemType="https://schema.org/Review"
        className={classes.container}
      >
        <div className={classes.heading}>
          <SdSummary review={maskedReview} />
          <SdInformation review={maskedReview}>
            <p>
              投稿日:
              <time itemProp="datePublished" dateTime={review.userPostDate}>
                {format(review.userPostDate, 'yyyy/MM/dd')}
              </time>
            </p>
          </SdInformation>
        </div>
        <ReviewComments review={maskedReview} />
      </article>
    </>
  )
}

function SdSummary({ review: _review }: { review: FragmentType<typeof ReviewsNodesFragment> }) {
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const classes = sdReviewComponentStyles()
  const totalValue = review.__typename === 'LyReview' ? review.totalValue : review.rating.totalValue

  return (
    <div className={classes.summary}>
      {review.__typename !== 'LyReview' && (
        <LabelNormal text={MEAL_PERIOD_LABEL[review.mealPeriod]} />
      )}
      <div
        itemProp="reviewRating"
        itemScope
        itemType="https://schema.org/Rating"
        className={classes.total}
      >
        <RatingStars value={totalValue} size="sm" />
        <div
          itemProp="ratingValue"
          className={css({
            textAlign: 'center',
            fontSize: 'lg',
            fontWeight: 'bold',
            lineHeight: 'none',
          })}
        >
          {totalValue.toFixed(1)}
        </div>
      </div>
    </div>
  )
}

const SdInformation: FC<InformationProps> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgSdInformation : IkyuSdInformation

function PpgSdInformation({ review: _review, children }: InformationProps) {
  const classes = sdReviewComponentStyles()
  const review = getFragmentData(ReviewsNodesFragment, _review)
  return (
    <div className={classes.information}>
      <HandleName handleName={review.handleName} />
      {children}
    </div>
  )
}

function IkyuSdInformation({ review: _review, children }: PropsWithChildren<InformationProps>) {
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const classes = sdReviewComponentStyles()

  if (review.__typename === 'LyReview' || isEmpty(review.memberHash)) {
    return (
      <div className={classes.information}>
        <HandleName handleName={review.handleName} />
        {children}
      </div>
    )
  }

  return (
    <div className={classes.information}>
      <p>
        <SafariNewTabLink href={`/reviews/user/${review.memberHash}/`}>
          <span itemProp="author" itemScope itemType="https://schema.org/Person">
            <span itemProp="name">{review.handleName}</span>
          </span>
        </SafariNewTabLink>
      </p>
      {children}
    </div>
  )
}
